// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ProseMirror JSON → HTML rendering utilities
// Extracted from DocumentViewerPage for reuse across viewer components
// ═══════════════════════════════════════════════════════════════

// ─── Content Block interface ───
export interface ContentBlock {
  html: string;
  isH1: boolean;
  isTable: boolean;
  sectionTitle: string;
}

// ─── TOC Item interface ───
export interface TocItem {
  id: string;
  number: string;
  title: string;
  level: number;
  children: TocItem[];
}

// ─── TipTap JSON → HTML renderer with auto-numbering ───
export function renderContentBlocks(json: any): ContentBlock[] {
  if (!json) return [];
  if (typeof json === 'string')
    return [{ html: json, isH1: false, isTable: false, sectionTitle: '' }];
  if (json.content) {
    const counters = { h1: 0, h2: 0, h3: 0 };
    const currentSection = '';
    const blocks = flattenNodes(json.content, counters, currentSection);
    return blocks;
  }
  return [];
}

function flattenNodes(
  nodes: any[],
  counters: { h1: number; h2: number; h3: number },
  currentSection: string,
): ContentBlock[] {
  if (!nodes) return [];
  const blocks: ContentBlock[] = [];
  for (const node of nodes) {
    switch (node.type) {
      case 'heading': {
        const level = node.attrs?.level ?? 1;
        const text = renderInline(node.content);
        const plainText = text.replace(/<[^>]+>/g, '');
        const cleanText = text.replace(/^(\d+\.)*\d+\s+/, '');
        const cleanPlain = plainText.replace(/^(\d+\.)*\d+\s+/, '');
        let prefix = '';
        if (level === 1) {
          counters.h1++;
          counters.h2 = 0;
          counters.h3 = 0;
          prefix = `${counters.h1} `;
          currentSection = `${counters.h1} ${cleanPlain}`;
        } else if (level === 2) {
          counters.h2++;
          counters.h3 = 0;
          prefix = `${counters.h1}.${counters.h2} `;
        } else if (level === 3) {
          counters.h3++;
          prefix = `${counters.h1}.${counters.h2}.${counters.h3} `;
        }
        const id = `${prefix.trim().replace(/\s+/g, '')}-${cleanPlain}`
          .replace(/\s+/g, '-')
          .toLowerCase();
        blocks.push({
          html: `<h${level} id="${id}">${prefix}${cleanText}</h${level}>`,
          isH1: level === 1,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      }
      case 'paragraph': {
        const pStyle = node.attrs?.pStyle ? ` class="ps-${node.attrs.pStyle}"` : '';
        const pAlign = node.attrs?.textAlign ? ` style="text-align:${node.attrs.textAlign}"` : '';
        blocks.push({
          html: `<p${pStyle}${pAlign}>${renderInline(node.content)}</p>`,
          isH1: false,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      }
      case 'bulletList':
        blocks.push({
          html: `<ul>${renderSubNodes(node.content, counters)}</ul>`,
          isH1: false,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      case 'orderedList':
        blocks.push({
          html: `<ol>${renderSubNodes(node.content, counters)}</ol>`,
          isH1: false,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      case 'table':
        blocks.push({
          html: `<table class="dtbl">${renderSubNodes(node.content, counters)}</table>`,
          isH1: false,
          isTable: true,
          sectionTitle: currentSection,
        });
        break;
      case 'blockquote':
        blocks.push({
          html: `<div class="callout">${renderSubNodes(node.content, counters)}</div>`,
          isH1: false,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      case 'horizontalRule':
        blocks.push({ html: '<hr/>', isH1: false, isTable: false, sectionTitle: currentSection });
        break;
      case 'codeBlock':
        blocks.push({
          html: `<pre><code>${renderInline(node.content)}</code></pre>`,
          isH1: false,
          isTable: false,
          sectionTitle: currentSection,
        });
        break;
      default:
        if (node.content) {
          blocks.push(...flattenNodes(node.content, counters, currentSection));
        }
        break;
    }
  }
  return blocks;
}

function cellStyle(attrs: any, isHeader: boolean = false): string {
  if (!attrs && !isHeader) return '';
  const parts: string[] = [];
  if (attrs?.background) parts.push(`background:${attrs.background}`);
  if (attrs?.textColor) parts.push(`color:${attrs.textColor}`);
  if (attrs?.colwidth) parts.push(`width:${attrs.colwidth}%`);
  if (isHeader) parts.push('text-align:center;vertical-align:middle');
  return parts.length > 0 ? ` style="${parts.join(';')}"` : '';
}

function renderSubNodes(nodes: any[], counters: { h1: number; h2: number; h3: number }): string {
  if (!nodes) return '';
  return nodes
    .map((node) => {
      switch (node.type) {
        case 'listItem':
          return `<li>${renderSubNodes(node.content, counters)}</li>`;
        case 'paragraph':
          return `<p>${renderInline(node.content)}</p>`;
        case 'bulletList':
          return `<ul>${renderSubNodes(node.content, counters)}</ul>`;
        case 'orderedList':
          return `<ol>${renderSubNodes(node.content, counters)}</ol>`;
        case 'tableRow':
          return `<tr>${renderSubNodes(node.content, counters)}</tr>`;
        case 'tableCell': {
          const style = cellStyle(node.attrs);
          return `<td${style}>${renderSubNodes(node.content, counters)}</td>`;
        }
        case 'tableHeader': {
          const style = cellStyle(node.attrs, true);
          return `<th${style}>${renderSubNodes(node.content, counters)}</th>`;
        }
        default:
          if (node.content) return renderSubNodes(node.content, counters);
          return '';
      }
    })
    .join('');
}

function renderInline(content: any[]): string {
  if (!content) return '';
  return content
    .map((node) => {
      if (node.type === 'text') {
        let text = escapeHtml(node.text ?? '');
        if (node.marks) {
          for (const mark of node.marks) {
            if (mark.type === 'bold') text = `<strong>${text}</strong>`;
            if (mark.type === 'italic') text = `<em>${text}</em>`;
            if (mark.type === 'underline') text = `<u>${text}</u>`;
            if (mark.type === 'strike') text = `<s>${text}</s>`;
            if (mark.type === 'link' && mark.attrs?.href) {
              const href = mark.attrs.href;
              if (href.startsWith('/documento/')) {
                text = `<a href="${escapeHtml(href)}" data-internal-link="true" style="color:#2F5496;text-decoration:underline;cursor:pointer">${text}</a>`;
              } else {
                text = `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="color:#2F5496;text-decoration:underline">${text}</a>`;
              }
            }
            if (mark.type === 'textStyle' && mark.attrs?.color)
              text = `<span style="color:${mark.attrs.color}">${text}</span>`;
          }
        }
        return text;
      }
      if (node.content) return renderInline(node.content);
      return '';
    })
    .join('');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Extract TOC from ProseMirror JSON ───
export function extractToc(json: any): TocItem[] {
  const items: TocItem[] = [];
  if (!json) return items;
  const nodes = typeof json === 'string' ? [] : (json.content ?? []);
  let h1Count = 0,
    h2Count = 0,
    h3Count = 0;

  for (const node of nodes) {
    if (node.type === 'heading') {
      const level = node.attrs?.level ?? 1;
      let text = (node.content ?? []).map((n: any) => n.text ?? '').join('');
      text = text.replace(/^(\d+\.)*\d+\s+/, '');

      if (level === 1) {
        h1Count++;
        h2Count = 0;
        h3Count = 0;
        const number = `${h1Count}`;
        const id = `${number}-${text}`.replace(/\s+/g, '-').toLowerCase();
        items.push({ id, number, title: text, level: 1, children: [] });
      } else if (level === 2 && items.length > 0) {
        h2Count++;
        h3Count = 0;
        const number = `${h1Count}.${h2Count}`;
        const id = `${number}-${text}`.replace(/\s+/g, '-').toLowerCase();
        items[items.length - 1].children.push({ id, number, title: text, level: 2, children: [] });
      } else if (level === 3 && items.length > 0) {
        const lastH1 = items[items.length - 1];
        if (lastH1.children.length > 0) {
          h3Count++;
          const number = `${h1Count}.${h2Count}.${h3Count}`;
          const id = `${number}-${text}`.replace(/\s+/g, '-').toLowerCase();
          lastH1.children[lastH1.children.length - 1].children.push({
            id,
            number,
            title: text,
            level: 3,
            children: [],
          });
        }
      }
    }
  }
  return items;
}
